import { match, P } from 'ts-pattern';
import CardSection from '~/components/CardSection';
import Disclosure from '~/components/Disclosure';
import Divide from '~/components/Divide';
import Icon, { CarrierIcon } from '~/components/Icon';
import LineItem from '~/components/LineItem';
import Link from '~/components/Link';
import PaginationControls from '~/components/pagination/PaginationControls';
import usePagination from '~/components/pagination/usePagination';
import { formatDate, formatDateTimeUtc } from '~/utils/formatters';
import { useLoaderData } from './orderHub';

type OrderHubLoaderData = ReturnType<typeof useLoaderData>;
type Tracker = OrderHubLoaderData['tracking']['trackers'][number];
type TrackerDetail = Tracker['details'][number];
type TrackerStatus = TrackerDetail['status'];

const statusLabels = {
  delivered: 'Delivered',
  available_for_pickup: 'Available for Pickup',
  cancelled: 'Cancelled',
  error: 'Error',
  failure: 'Failure',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  pre_transit: 'Getting Ready To Ship',
  return_to_sender: 'Return to Sender',
  unknown: 'Unknown',
} satisfies Record<TrackerStatus, string>;

const deliveredOnText = (
  trackingDetail: TrackerDetail & { estimatedDeliveryDate?: string | null },
) =>
  match(trackingDetail)
    // if latest status is delivered, show the date it was delivered
    .with({ status: 'delivered' }, ({ date }) => formatDateTimeUtc(date))
    // if latest status is not delivered, show the estimated delivery date if we have one
    .with({ estimatedDeliveryDate: P.string }, ({ estimatedDeliveryDate }) =>
      formatDate(estimatedDeliveryDate),
    )
    .with({ status: 'pre_transit' }, () => 'In Progress')
    .otherwise(() => 'Unavailable');

const statusHeaderText = (latestDetailEvent?: TrackerDetail) =>
  match(latestDetailEvent)
    .with(P.nullish, () => 'Awaiting Details')
    .with({ status: 'delivered' }, () => 'Delivered On')
    .otherwise(() => 'Estimated Delivery');

function CarrierDetail({ tracker }: { tracker: Tracker }) {
  const { carrier } = tracker;
  const { name, trackingCode, trackingUrl } = carrier;

  const trackableUrl =
    trackingUrl ?? `https://www.google.com/search?q=${trackingCode}`;

  return (
    <CardSection
      className="grid-cols-[auto_1fr_auto] gap-y-0"
      media={<CarrierIcon carrier={name} className="size-9" />}
      title={name}
      action={
        <Link
          to={trackableUrl}
          reloadDocument
          target="_blank"
          rel="noreferrer"
          // wrapping anywhere allows long tracking codes to break onto multiple lines, useful when out of room even to grow the column; i.e. mobile
          className="wrap-anywhere sm:justify-self-end"
        >
          {trackingCode}
        </Link>
      }
    />
  );
}

function Event({ event }: { event: TrackerDetail }) {
  const {
    status,
    message,
    date,
    location: { city, state },
  } = event;

  return (
    <div>
      <div className="label-md text-on-surface">
        {formatDate(date)}
        {city && state && <span>{` | ${city}, ${state}`}</span>}
      </div>
      <div className="body-md text-on-surface-variant">{`${statusLabels[status]} - ${message}`}</div>
    </div>
  );
}

function HistoryDetail({
  events: [latestEvent, ...history],
}: {
  events: TrackerDetail[];
}) {
  return latestEvent ? (
    <Disclosure summary={<Event event={latestEvent} />}>
      <Divide
        as="ul"
        spacing="compact"
        ariaLabel="Shipping Events"
        className="mt-4"
      >
        {history.map((trackingDetail) => (
          <li key={trackingDetail.date}>
            <Event event={trackingDetail} />
          </li>
        ))}
      </Divide>
    </Disclosure>
  ) : null;
}

function StatusDisplay({ tracker }: { tracker: Tracker }) {
  const {
    estimatedDeliveryDate,
    details: [latestDetailEvent],
    status: currentStatus,
  } = tracker;
  const deliverStatusText = latestDetailEvent
    ? deliveredOnText({ ...latestDetailEvent, estimatedDeliveryDate })
    : 'Order Placed';

  return (
    <div className="text-on-surface">
      <p className="subheading-sm">{statusHeaderText(latestDetailEvent)}</p>
      <p className="heading-lg">{deliverStatusText}</p>
      <p className="text-on-surface-variant">
        {latestDetailEvent
          ? statusLabels[currentStatus]
          : statusLabels.pre_transit}
      </p>
    </div>
  );
}

function NoTracker() {
  return (
    <div className="flex items-center gap-2 border-none label-md text-on-surface">
      <Icon icon="InfoCircle" className="size-6 shrink-0" />
      Tracking information not available
    </div>
  );
}

function LineItemDisplay({
  lineItems,
}: {
  lineItems: Tracker['lineItemsInShipment'];
}) {
  const totalItems = lineItems.reduce((acc, item) => acc + item.quantity, 0);
  return (
    <Disclosure
      summary={
        <span className="flex items-center gap-2">
          {totalItems} item{totalItems > 1 && 's'} in this shipment
        </span>
      }
    >
      <div className="mt-8 space-y-4">
        {lineItems.map((item) => (
          <LineItem
            key={item.id}
            variant="small"
            name={item.name ?? 'N/A'}
            options={item.optionsFromPlatform}
            imgSrc={item.imgUrl}
            quantity={item.quantity}
          />
        ))}
      </div>
    </Disclosure>
  );
}

export default function Tracking({ trackers }: { trackers: Tracker[] }) {
  const [pageItems, pagination] = usePagination({
    items: trackers,
    itemsPerPage: 1,
  });
  const selectedTracker = pageItems[0];

  return selectedTracker ? (
    <section
      className="flex flex-col gap-4"
      data-testid={selectedTracker.idFromPlatform}
      aria-labelledby={selectedTracker.idFromPlatform}
    >
      <h3 id={selectedTracker.idFromPlatform} className="sr-only">
        Current Shipment
      </h3>
      {pagination.pageCount > 1 && (
        <div className="flex flex-col items-center justify-center md:flex-row md:justify-between">
          <div className="mb-4 flex items-center gap-2 label-lg md:mb-0">
            <Icon icon="InfoCircle" className="size-6 shrink-0" />
            This order contains {trackers.length} shipments
          </div>
          <PaginationControls
            loop
            // oxlint-disable-next-line react/jsx-props-no-spreading -- pagination controls props are the same as pagination object
            {...pagination}
          />
        </div>
      )}
      <Divide>
        <StatusDisplay tracker={selectedTracker} />

        {selectedTracker.details.length > 0 && (
          <HistoryDetail events={selectedTracker.details} />
        )}
        <CarrierDetail tracker={selectedTracker} />

        {selectedTracker.lineItemsInShipment.length > 0 && (
          <LineItemDisplay lineItems={selectedTracker.lineItemsInShipment} />
        )}
      </Divide>
    </section>
  ) : (
    <NoTracker />
  );
}
